import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isApiError, transactionApi } from "../api";
import type { SavedPaymentMethodResponse } from "../types/generated";
import {
  beginPaymentMethodRemoval,
  capturePaymentMethodDetachBarrierGeneration,
  completePaymentMethodRemoval,
  detachedPaymentMethodIdsQueryKey,
  finishPaymentMethodRemoval,
  invalidatePaymentMethodRemovalQueries,
  isDefinitivePaymentMethodRemovalFailure,
  isPaymentMethodDetachBarrierGenerationCurrent,
  isPaymentMethodRemovalOperationCurrentForQuery,
  isSamePaymentMethodIdentity,
  paymentMethodBarrierScope,
  paymentMethodQueryScope,
  PaymentMethodRemovalPersistenceError,
  readKnownDetachedPaymentMethodIds,
  readKnownUncertainPaymentMethodIds,
  rejectPaymentMethodRemoval,
  removingPaymentMethodIdsQueryKey,
  uncertainPaymentMethodIdsQueryKey,
} from "../hooks/payment/paymentMethodDetachBarrier";
import {
  formatSavedPaymentMethodExpiry,
  getSavedPaymentMethodUnavailableLabel,
} from "../utils/paymentSelection";

type PaymentMethodPickerBaseProps = {
  methods: SavedPaymentMethodResponse[];
  selectedId: string | null;
  /** Receives the chosen payment_method_id — or null when the selected card was removed. */
  onSelect: (paymentMethodId: string | null) => void;
  disabled?: boolean;
  /** App-native presentation. Selection/removal safety remains in this shared
   * controller; the consumer only renders its supplied model. */
  render: (model: PaymentMethodPickerRenderModel) => ReactNode;
};

export interface PaymentMethodPickerRenderRow {
  method: SavedPaymentMethodResponse;
  cardName: string;
  expiry: string | null;
  expiryDescription: string;
  unavailableLabel: string | null;
  accessibleLabel: string;
  selected: boolean;
  removing: boolean;
  uncertain: boolean;
  confirming: boolean;
  disabled: boolean;
  errorMessage: string | null;
  canRemove: boolean;
  removeLabel: string;
  removeDisabled: boolean;
  confirmDisabled: boolean;
  keepDisabled: boolean;
  select: () => void;
  beginRemove: () => void;
  confirmRemove: () => void;
  keepCard: () => void;
  setRemoveButton: (node: HTMLButtonElement | null) => void;
  setConfirmButton: (node: HTMLButtonElement | null) => void;
}

export interface PaymentMethodPickerRenderModel {
  announcement: string | null;
  radioGroupName: string;
  empty: boolean;
  setEmptyElement: (node: HTMLElement | null) => void;
  setGroupElement: (node: HTMLElement | null) => void;
  rows: PaymentMethodPickerRenderRow[];
}

/**
 * The remove affordance is all-or-nothing: enabling `allowRemove` REQUIRES the
 * facility scope for the DELETE and the query key to invalidate afterwards — a
 * remove that cannot refresh the list it came from is incoherent. Omitting
 * `allowRemove` (or passing false) keeps the picker pure presentation.
 */
type PaymentMethodPickerRemoveProps =
  | {
      /** Default false: no remove affordance. */
      allowRemove?: false;
      facilityId?: string;
      paymentMethodsQueryKey?: readonly unknown[];
    }
  | {
      allowRemove?: boolean;
      /** Scopes the DELETE to this facility's connected account. */
      facilityId: string;
      /** Query key invalidated after a successful DELETE (the page's payment-methods list). */
      paymentMethodsQueryKey: readonly unknown[];
    };

export type PaymentMethodPickerProps = PaymentMethodPickerBaseProps &
  PaymentMethodPickerRemoveProps;

function paymentMethodControlKey(
  scope: string,
  paymentMethodId: string,
): string {
  return `${scope}\u0000${paymentMethodId}`;
}

/**
 * Radio list of the user's SAVED cards at a facility (Stripe Connect connected
 * account). Canonical shared copy — used by every facility-scoped card-payment
 * surface (booking checkout, class enrollment, …). Selecting a card yields its
 * payment_method_id, which is what the page's confirm call charges. An "add a
 * new card" affordance is rendered separately (AddCardForm) by the page.
 *
 * Each persistent native radio carries the brand + last4 in its accessible name,
 * so the selection is visible, directly operable, and readable by assistive
 * tech. No fabricated card data — only what the backend returned — and never a
 * raw pm_ id as visible copy. The Remove button is a SIBLING of the radio's
 * <label>, never inside it: a click inside the label would also toggle the
 * money-bearing selection.
 *
 * Remove flow (opt-in via `allowRemove`):
 *   per-card "Remove {brand} ending {last4}, expires {M/YYYY}" (the expiry
 *   disambiguates duplicate same-brand+last4 cards) → an inline confirm step
 *   with honest copy ("Keep card" backs out with zero network) → on confirm,
 *   transactionApi.removePaymentMethod(facilityId, pmId). On success — a
 *   DELETE 2xx is SERVER TRUTH, not optimism:
 *     - the SELECTION is reset immediately when the removed card was the
 *       selected one (read off the LATEST selection via a ref, never a stale
 *       closure); only THAT card's confirm step closes (another card's open
 *       confirm is the user's in-progress decision);
 *     - the detach is written into the SHARED query cache (setQueryData
 *       filtering the id out of `paymentMethodsQueryKey`) BEFORE invalidating,
 *       so every consumer of the key — pages reading the raw query data, other
 *       surfaces like the claim flow — sees the truth immediately; the
 *       follow-up invalidation still reconciles with the server;
 *     - the id joins a connected-account-scoped detached set shared by every
 *       facility alias of that organization wallet: a known-detached card
 *       never renders as selectable again, even when a FAILED or eventually
 *       consistent STALE refetch re-lists it (that selection is what the
 *       page's confirm call charges);
 *     - the shared payment-method list is filtered again AFTER reconciliation,
 *       so a successful stale refetch cannot re-arm a page-level auto-select.
 *   No-optimistic RENDERING is preserved: while the remove is settling, the
 *   row stays visible (disabled) — re-inserted from a snapshot when the cache
 *   already dropped it — and leaves only once the refetch settles.
 *   A GUARD EFFECT makes the detached contract CONTINUOUS, not one-shot: any
 *   parent (re)asserting a known-detached `selectedId` (e.g. a page-level
 *   auto-select reading a stale-but-successful refetch) is rejected with
 *   onSelect(null).
 *   Confirmation disarms a selected card before issuing DELETE. A definitive
 *   failed DELETE surfaces a retryable alert; an ambiguous network/provider
 *   outcome remains disabled for the authenticated tab session. When a
 *   removed row unmounts, focus moves to the
 *   radiogroup (or the empty state) and an aria-live region announces the
 *   removal — keyed so an identical second announcement still fires.
 */
export function PaymentMethodPicker(props: PaymentMethodPickerProps) {
  const {
    methods,
    selectedId,
    onSelect,
    disabled,
    allowRemove,
    facilityId,
    paymentMethodsQueryKey,
    render,
  } = props;
  const radioGroupName = `saved-card-${useId()}`;
  const queryClient = useQueryClient();
  const authGeneration = capturePaymentMethodDetachBarrierGeneration();
  const scopedPaymentMethodsQueryKey =
    paymentMethodsQueryKey ?? (["unscoped"] as const);
  const queryScope = paymentMethodQueryScope(scopedPaymentMethodsQueryKey);
  const barrierScope = paymentMethodBarrierScope(
    scopedPaymentMethodsQueryKey,
    methods,
  );
  const controlScope = `${authGeneration}\u0000${queryScope}\u0000${barrierScope}`;
  const detachedIdsQueryKey = detachedPaymentMethodIdsQueryKey(
    scopedPaymentMethodsQueryKey,
    methods,
  );
  const uncertainIdsQueryKey = uncertainPaymentMethodIdsQueryKey(
    scopedPaymentMethodsQueryKey,
    methods,
  );
  const removingIdsQueryKey = removingPaymentMethodIdsQueryKey(
    scopedPaymentMethodsQueryKey,
    methods,
  );
  const { data: sharedDetachedIds = [] } = useQuery<string[]>({
    queryKey: detachedIdsQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: () =>
      readKnownDetachedPaymentMethodIds(
        queryClient,
        scopedPaymentMethodsQueryKey,
        methods,
      ),
    gcTime: Infinity,
  });
  const { data: sharedRemovingIds = [] } = useQuery<string[]>({
    queryKey: removingIdsQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: [],
    gcTime: Infinity,
  });
  const { data: sharedUncertainIds = [] } = useQuery<string[]>({
    queryKey: uncertainIdsQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: () =>
      readKnownUncertainPaymentMethodIds(
        queryClient,
        scopedPaymentMethodsQueryKey,
        methods,
      ),
    gcTime: Infinity,
  });
  // Which card's inline remove-confirm step is open (at most one).
  const [confirmingCard, setConfirming] = useState<{
    scope: string;
    paymentMethodId: string;
  } | null>(null);
  // Which card's DELETE (+ awaited refetch) is in flight — that row stays disabled.
  const [removingCard, setRemoving] = useState<{
    scope: string;
    paymentMethodId: string;
  } | null>(null);
  // Per-card failure: pinning the pmId keeps the alert in the row that actually
  // failed — never in whichever row happens to have its confirm step open.
  const [removeError, setRemoveError] = useState<{
    scope: string;
    pmId: string;
    message: string;
  } | null>(null);
  // Screen-reader announcement for a removed (about-to-unmount) row. The nonce
  // keys re-announcement: an aria-live region only announces on CONTENT change,
  // so removing two identically-named cards must not produce a silent second
  // removal (a zero-width space toggles the text node without visible change).
  const [announcement, setAnnouncement] = useState<{
    scope: string;
    text: string;
    nonce: number;
  } | null>(null);
  // Keep a synchronous local mirror alongside the shared wallet tombstones.
  // The mirror closes the current event-loop turn; the shared list makes the
  // same server truth observable to every picker for this connected account.
  const detachedIdsRef = useRef<Map<string, Set<string>>>(new Map());
  const detachedIdsForScope = () => {
    const existing = detachedIdsRef.current.get(controlScope);
    if (existing) return existing;
    const created = new Set<string>();
    detachedIdsRef.current.set(controlScope, created);
    return created;
  };
  const isKnownDetached = (paymentMethodId: string) =>
    detachedIdsForScope().has(paymentMethodId) ||
    sharedDetachedIds.includes(paymentMethodId);
  const isKnownUncertain = (paymentMethodId: string) =>
    sharedUncertainIds.includes(paymentMethodId);
  // Snapshot of the card being removed (+ its display position): the shared
  // cache drops the card the moment the DELETE succeeds (server-truth
  // application), but the no-optimistic RENDERING contract keeps the row
  // visible (disabled) until the refetch settles — so it renders from here.
  const removingSnapRef = useRef<{
    scope: string;
    card: SavedPaymentMethodResponse;
    index: number;
  } | null>(null);
  const uncertainSnapRef = useRef<{
    scope: string;
    card: SavedPaymentMethodResponse;
    index: number;
  } | null>(null);
  // Latest selection for the async success path — never decide the reset off a
  // stale closure (the selection can change while the DELETE is in flight).
  const selectedIdRef = useRef(selectedId);
  // Immutable recovery may intentionally keep rendering the same captured id.
  // Notify its parent once, then wait for the rendered selection to change.
  const rejectedSelectionRef = useRef<string | null>(null);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useLayoutEffect(() => {
    setConfirming((current) =>
      current?.scope === controlScope ? current : null,
    );
    setRemoveError((current) =>
      current?.scope === controlScope ? current : null,
    );
    if (uncertainSnapRef.current?.scope !== controlScope) {
      uncertainSnapRef.current = null;
    }
    setAnnouncement(null);
  }, [controlScope]);

  // CONTINUOUS detached-selection guard: the one-shot onSelect(null) at DELETE
  // success is not enough — a parent auto-select reading the RAW query data
  // can re-assert the detached id after an eventually-consistent refetch
  // resolves still listing the card. The picker knows that id is detached and
  // would not even render its row; an invisible armed selection must never
  // survive a commit. Reject it.
  //
  // DELIBERATELY no dependency array — the check must run on EVERY commit.
  // React batching can collapse the whole remove (reset to null, refetch
  // landing, a parent's render-phase auto-select re-arming the id) into ONE
  // commit whose selectedId equals the previously committed value; a
  // [selectedId]-gated effect never fires across that collapse and the armed
  // detached id survives. The per-commit Set lookup is trivially cheap.
  useLayoutEffect(() => {
    const selectedMethod =
      selectedId === null
        ? undefined
        : methods.find((method) => method.payment_method_id === selectedId);
    const selectionRejected =
      selectedId !== null &&
      (isKnownDetached(selectedId) ||
        isKnownUncertain(selectedId) ||
        sharedRemovingIds.includes(selectedId) ||
        selectedMethod?.is_usable === false);
    if (!selectionRejected) {
      rejectedSelectionRef.current = null;
      return;
    }
    if (rejectedSelectionRef.current !== selectedId) {
      rejectedSelectionRef.current = selectedId;
      onSelect(null);
    }
  });

  // Destructive confirmation disables or unmounts the activating control.
  // Park focus during the request, then restore it to the actionable control
  // for failure/cancel or to the remaining group after success.
  const groupRef = useRef<HTMLElement | null>(null);
  const emptyRef = useRef<HTMLElement | null>(null);
  const removeButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const confirmButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const focusAfterRemoveRef = useRef<{
    scope: string;
    paymentMethodId: string;
    target: "confirm" | "group" | "remove";
  } | null>(null);
  useLayoutEffect(() => {
    const request = focusAfterRemoveRef.current;
    if (request === null || removingCard?.scope === request.scope) return;
    if (request.scope !== controlScope) return;

    const restoreFocus = () => {
      if (focusAfterRemoveRef.current !== request) return;
      const activeElement = document.activeElement;
      const mayRestore =
        activeElement === document.body ||
        activeElement === document.documentElement ||
        activeElement === groupRef.current ||
        activeElement?.getAttribute("role") === "radiogroup";
      if (!mayRestore) return;

      const key = paymentMethodControlKey(
        request.scope,
        request.paymentMethodId,
      );
      const target =
        request.target === "confirm"
          ? confirmButtonRefs.current.get(key)
          : request.target === "remove"
            ? removeButtonRefs.current.get(key)
            : (groupRef.current ?? emptyRef.current);
      if (target === undefined || target === null) return;
      focusAfterRemoveRef.current = null;
      target.focus();
    };

    restoreFocus();
    // Inline callback refs are refreshed after cache-driven row replacement.
    // If the target was between detach/attach during this layout phase, retry
    // once on the next frame without stealing focus if the user moved away.
    if (focusAfterRemoveRef.current !== request) return;
    const frame = window.requestAnimationFrame(restoreFocus);
    return () => window.cancelAnimationFrame(frame);
  }, [confirmingCard, controlScope, removeError, removingCard]);

  const canRemove = Boolean(
    allowRemove && facilityId && paymentMethodsQueryKey,
  );

  async function handleConfirmRemove(
    card: SavedPaymentMethodResponse,
    displayIndex: number,
  ) {
    if (!facilityId) return;
    const operationGeneration = capturePaymentMethodDetachBarrierGeneration();
    if (operationGeneration !== authGeneration) return;
    const pmId = card.payment_method_id;
    const operationScope = controlScope;
    const operationQueryKey = scopedPaymentMethodsQueryKey;
    const operationMethods = methods;
    let operation;
    try {
      operation = beginPaymentMethodRemoval(
        queryClient,
        operationQueryKey,
        operationMethods,
        pmId,
        operationGeneration,
      );
    } catch {
      setRemoveError({
        scope: operationScope,
        pmId,
        message:
          "We couldn't safely start removing this card. Please try again.",
      });
      focusAfterRemoveRef.current = {
        scope: operationScope,
        paymentMethodId: pmId,
        target: "confirm",
      };
      return;
    }
    if (operation === null) return;
    if (selectedIdRef.current === pmId) {
      selectedIdRef.current = null;
      onSelect(null);
    }
    groupRef.current?.focus();
    removingSnapRef.current = {
      scope: operationScope,
      card,
      index: displayIndex,
    };
    setRemoving({ scope: operationScope, paymentMethodId: pmId });
    setRemoveError(null);
    try {
      await transactionApi.removePaymentMethod(facilityId, pmId);
      if (!isPaymentMethodDetachBarrierGenerationCurrent(operationGeneration)) {
        return;
      }
      // DELETE 2xx — the card IS detached at Stripe (server truth). Record it
      // so the row can never be offered again regardless of refetch outcome,
      // and reset the selection immediately if it pointed at this card — a
      // detached card must never stay selected (and chargeable), not even
      // during the refetch window.
      const localIds =
        detachedIdsRef.current.get(operationScope) ?? new Set<string>();
      localIds.add(pmId);
      detachedIdsRef.current.set(operationScope, localIds);
      try {
        completePaymentMethodRemoval(queryClient, operation);
      } catch (error) {
        if (!(error instanceof PaymentMethodRemovalPersistenceError)) {
          throw error;
        }
        // DELETE already returned 2xx. The durable pre-dispatch uncertain
        // fence is safer than rejecting the operation and restoring a card
        // that Stripe has detached; keep that fence and the local 2xx truth.
      }
      if (paymentMethodsQueryKey) {
        // Every facility alias registered for this connected account shares
        // the same server truth. Filter first, then reconcile every alias and
        // fence stale successful responses before any checkout can auto-select.
        await invalidatePaymentMethodRemovalQueries(
          queryClient,
          operation,
        ).catch(() => undefined);
      }
      if (!isPaymentMethodDetachBarrierGenerationCurrent(operationGeneration)) {
        return;
      }
      // Close THIS card's confirm step only — another card's open confirm is
      // the user's in-progress decision and must survive.
      setConfirming((current) =>
        current?.scope === operationScope && current.paymentMethodId === pmId
          ? null
          : current,
      );
      setAnnouncement((prev) => ({
        scope: operationScope,
        text: `Card removed: ${card.brand} ending ${card.last4}, expires ${card.exp_month}/${card.exp_year}.`,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
      focusAfterRemoveRef.current = {
        scope: operationScope,
        paymentMethodId: pmId,
        target: "group",
      };
    } catch (error) {
      if (!isPaymentMethodDetachBarrierGenerationCurrent(operationGeneration)) {
        return;
      }
      // Only a narrow, explicit client-error response establishes that the
      // provider did not detach the card. A network/5xx/ownership ambiguity
      // stays fenced for this authenticated tab session: charging an uncertain
      // card is worse than asking the player to use another one.
      const failureIsDefinitive =
        isDefinitivePaymentMethodRemovalFailure(error);
      if (failureIsDefinitive) {
        const fenceCleared = rejectPaymentMethodRemoval(queryClient, operation);
        if (
          fenceCleared &&
          isPaymentMethodRemovalOperationCurrentForQuery(operation) &&
          queryClient.getQueryState(operationQueryKey)?.status !== "error"
        ) {
          queryClient.setQueryData<SavedPaymentMethodResponse[]>(
            operationQueryKey,
            (old) => {
              if (
                old?.some((method) => isSamePaymentMethodIdentity(method, card))
              ) {
                return old;
              }
              const restored = [...(old ?? [])];
              restored.splice(Math.min(displayIndex, restored.length), 0, card);
              return restored;
            },
          );
        }
        void invalidatePaymentMethodRemovalQueries(
          queryClient,
          operation,
        ).catch(() => undefined);
      } else {
        uncertainSnapRef.current = {
          scope: operationScope,
          card,
          index: displayIndex,
        };
        setConfirming((current) =>
          current?.scope === operationScope && current.paymentMethodId === pmId
            ? null
            : current,
        );
      }
      setRemoveError({
        scope: operationScope,
        pmId,
        message: failureIsDefinitive
          ? isApiError(error) && error.code === "PAYMENT_METHOD_IN_USE"
            ? error.message
            : "We couldn't remove this card. Please try again."
          : "We couldn't verify whether this card was removed. For safety, use another card.",
      });
      focusAfterRemoveRef.current = {
        scope: operationScope,
        paymentMethodId: pmId,
        target: failureIsDefinitive ? "confirm" : "group",
      };
    } finally {
      if (isPaymentMethodDetachBarrierGenerationCurrent(operationGeneration)) {
        finishPaymentMethodRemoval(queryClient, operation);
      }
      if (
        removingSnapRef.current?.scope === operationScope &&
        removingSnapRef.current.card.payment_method_id === pmId
      ) {
        removingSnapRef.current = null;
      }
      setRemoving((current) =>
        current?.scope === operationScope && current.paymentMethodId === pmId
          ? null
          : current,
      );
    }
  }

  // Known-detached cards never render — EXCEPT while their own remove is still
  // settling (`removingId`), which preserves the no-optimistic contract: the
  // row stays visible (disabled) until the refetch settles, then leaves for
  // good whether the refetch succeeded, failed, or returned a stale list.
  const removingId =
    removingCard?.scope === controlScope ? removingCard.paymentMethodId : null;
  const visibleMethods = methods.filter(
    (m) =>
      !isKnownDetached(m.payment_method_id) ||
      removingId === m.payment_method_id,
  );
  // The settling row must stay rendered even though the shared cache already
  // dropped it (server-truth application above) — re-insert it from the
  // snapshot at its original position until the refetch settles.
  let displayMethods = visibleMethods;
  const snap = removingSnapRef.current;
  if (
    removingId !== null &&
    snap !== null &&
    snap.scope === controlScope &&
    !visibleMethods.some((m) => m.payment_method_id === removingId)
  ) {
    displayMethods = [...visibleMethods];
    displayMethods.splice(
      Math.min(snap.index, displayMethods.length),
      0,
      snap.card,
    );
  }
  const uncertainSnap = uncertainSnapRef.current;
  if (
    uncertainSnap !== null &&
    uncertainSnap.scope === controlScope &&
    !displayMethods.some(
      (method) =>
        method.payment_method_id === uncertainSnap.card.payment_method_id,
    )
  ) {
    displayMethods = [...displayMethods];
    displayMethods.splice(
      Math.min(uncertainSnap.index, displayMethods.length),
      0,
      uncertainSnap.card,
    );
  }

  const announcementText =
    announcement?.scope === controlScope
      ? announcement.text + "​".repeat(announcement.nonce % 2)
      : null;
  const rows: PaymentMethodPickerRenderRow[] = displayMethods.map((m, idx) => {
    const cardName = `${m.brand} ending ${m.last4}`;
    const expiry = formatSavedPaymentMethodExpiry(m.exp_month, m.exp_year);
    const expiryDescription = expiry
      ? `expires ${expiry}`
      : "expiry unavailable";
    const unavailableLabel = getSavedPaymentMethodUnavailableLabel(
      m.unavailable_reason,
    );
    const accessibleLabel = unavailableLabel
      ? `${cardName}, ${expiryDescription}, ${unavailableLabel}`
      : `${cardName}, ${expiryDescription}`;
    const selected = m.is_usable && selectedId === m.payment_method_id;
    const rowRemoving = sharedRemovingIds.includes(m.payment_method_id);
    const rowUncertain = isKnownUncertain(m.payment_method_id);
    const rowConfirming =
      confirmingCard?.scope === controlScope &&
      confirmingCard.paymentMethodId === m.payment_method_id;
    const rowDisabled = Boolean(
      disabled || rowRemoving || rowUncertain || !m.is_usable,
    );
    const rowError =
      removeError?.scope === controlScope &&
      removeError.pmId === m.payment_method_id
        ? removeError.message
        : null;
    const controlKey = paymentMethodControlKey(
      controlScope,
      m.payment_method_id,
    );

    return {
      method: m,
      cardName,
      expiry,
      expiryDescription,
      unavailableLabel,
      accessibleLabel,
      selected,
      removing: rowRemoving,
      uncertain: rowUncertain,
      confirming: rowConfirming,
      disabled: rowDisabled,
      errorMessage: rowError,
      canRemove,
      removeLabel: `Remove ${cardName}, ${expiryDescription}`,
      removeDisabled: Boolean(disabled || rowRemoving || rowUncertain),
      confirmDisabled: Boolean(
        disabled ||
          removingCard?.scope === controlScope ||
          sharedRemovingIds.length > 0,
      ),
      keepDisabled: Boolean(disabled || rowRemoving),
      select: () => {
        if (m.is_usable) onSelect(m.payment_method_id);
      },
      beginRemove: () => {
        setConfirming({
          scope: controlScope,
          paymentMethodId: m.payment_method_id,
        });
        setRemoveError(null);
      },
      confirmRemove: () => void handleConfirmRemove(m, idx),
      keepCard: () => {
        focusAfterRemoveRef.current = {
          scope: controlScope,
          paymentMethodId: m.payment_method_id,
          target: "remove",
        };
        setConfirming(null);
        setRemoveError(null);
      },
      setRemoveButton: (node) => {
        if (node) removeButtonRefs.current.set(controlKey, node);
        else removeButtonRefs.current.delete(controlKey);
      },
      setConfirmButton: (node) => {
        if (node) confirmButtonRefs.current.set(controlKey, node);
        else confirmButtonRefs.current.delete(controlKey);
      },
    };
  });

  return render({
    announcement: announcementText,
    radioGroupName,
    empty: rows.length === 0,
    setEmptyElement: (node) => {
      emptyRef.current = node;
    },
    setGroupElement: (node) => {
      groupRef.current = node;
    },
    rows,
  });
}
